import { Link } from "react-router-dom";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import {
  SLACK_INSTALL_URL,
  SLACK_MARKETPLACE_APPROVED,
  SUPPORT_EMAIL,
  SUPPORT_HOURS,
  SUPPORT_RESPONSE,
} from "@/lib/publicSite";

const GUIDE_TOC = [
  { id: "prerequisites", label: "Prerequisites" },
  { id: "add", label: "Adding the app" },
  { id: "use", label: "Using Hub with Slack" },
  { id: "remove", label: "Removing the app" },
  { id: "data", label: "Your data after removal" },
  { id: "troubleshooting", label: "Troubleshooting" },
  { id: "support", label: "Support" },
] as const;

export function DocsSlackPage() {
  return (
    <PublicLayout title="ATW Hub for Slack — Integration guide">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Guide"
          title="ATW Hub for Slack"
          lede="How to add, use, and remove the ATW Hub app for Slack with ATW Hub."
          updated="October 1, 2026"
          marks={<ConnectionBrandIcon id="slack" size={40} />}
        />

        <PublicDocLayout toc={[...GUIDE_TOC]}>
          <section id="prerequisites" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="prerequisites" />
              Prerequisites
            </h2>
            <ul className="public-list">
              <li>An ATW account with Hub enabled for your organization.</li>
              <li>Permission to install Slack apps in your workspace (or an admin who can approve).</li>
              <li>
                A modern browser. Sign-in uses auth.atw.net ({SUPPORT_EMAIL} can help if Hub is not enabled
                for you).
              </li>
            </ul>
          </section>

          <section id="add" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="add" />
              Adding the app
            </h2>
            <ol className="public-steps">
              <li>
                Open <Link to="/slack">hub.atw.net/slack</Link> or, in Hub, go to Settings → Connections →
                Chat → <strong>ATW Hub for Slack</strong> → <strong>Connect Slack</strong>.
              </li>
              <li>
                You are sent to <a href={SLACK_INSTALL_URL}>api.hub.atw.net/slack/install</a>, then to
                Slack's consent screen. Choose your workspace and click <strong>Allow</strong>.
              </li>
              <li>
                If you are not signed in to Hub, sign in at auth.atw.net when prompted. Hub finishes
                connecting Slack to your Hub account automatically.
              </li>
              <li>
                In Slack, open the <strong>ATW Hub</strong> app and its <strong>Home</strong> tab for
                getting-started steps.
              </li>
            </ol>
            <p>
              If your workspace requires admin approval, ask a Slack admin to approve ATW Hub before you
              connect.
            </p>
          </section>

          <section id="use" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="use" />
              Using Hub with Slack
            </h2>
            <h3>Chat in Hub</h3>
            <p>
              Open <strong>Chat</strong> in Hub to see channels, private channels, DMs, and group DMs you
              are in. Open a conversation to load messages from Slack (Hub does not store message text).
              Reply, start DMs, and edit or delete your own messages from Hub.
            </p>
            <h3>Needs you (Hub Home)</h3>
            <p>
              {SLACK_MARKETPLACE_APPROVED
                ? "On Hub Home, Needs you lists recent mentions and DMs waiting on your reply."
                : "Needs you uses Slack search and is available after ATW Hub is approved in the Slack Marketplace. Until then, Hub Home explains the limit."}
            </p>
            <h3>Send to Hub</h3>
            <p>
              In Slack, open a message's <strong>More actions</strong> menu and choose{" "}
              <strong>Send to Hub</strong>. Hub saves a link to the message on Hub Home (not the message
              text).
            </p>
            <h3>Hub AI</h3>
            <p>
              In Hub, open <strong>Hub AI</strong> to ask questions about your Slack. Hub AI reads Slack
              only when you ask, through Slack's MCP server, and sends a message only when you press{" "}
              <strong>Send</strong> on a draft. AI can be wrong — review answers before you rely on them.
            </p>
          </section>

          <section id="remove" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="remove" />
              Removing the app
            </h2>
            <h3>Disconnect only your account</h3>
            <p>
              In Hub: Settings → Connections → Chat → <strong>Disconnect</strong> next to the Slack
              workspace. Hub revokes its Slack tokens and deletes read markers and saved links for that
              account. Your Slack messages are unchanged.
            </p>
            <h3>Uninstall from the whole workspace</h3>
            <ol className="public-steps">
              <li>In Slack (desktop): workspace name → Tools &amp; settings → Manage apps.</li>
              <li>Open <strong>ATW Hub</strong> → App Details → Configuration → <strong>Remove App</strong>.</li>
            </ol>
            <p>
              When Slack notifies Hub that the app was removed, Hub deletes tokens, read markers, and saved
              links for everyone in that workspace.
            </p>
          </section>

          <section id="data" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="data" />
              Your data after removal
            </h2>
            <p>
              Disconnecting or uninstalling removes Hub's Slack tokens, read markers, and saved links from
              Hub's servers. Hub never stored Slack message text. Messages you sent from Hub remain normal
              Slack messages. See the <Link to="/privacy">Privacy Policy</Link> for details.
            </p>
          </section>

          <section id="troubleshooting" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="troubleshooting" />
              Troubleshooting
            </h2>
            <ul className="public-list">
              <li>
                <strong>Connect Slack expired:</strong> start Connect Slack again from Settings →
                Connections → Chat.
              </li>
              <li>
                <strong>Slack rate limits:</strong>{" "}
                {SLACK_MARKETPLACE_APPROVED
                  ? "If Slack returns a rate limit, wait and try again, or open the conversation in Slack."
                  : "Before Marketplace approval, Slack may limit how often Hub can load history. Wait a minute and open the conversation again."}
              </li>
              <li>
                <strong>Guests:</strong> Slack guests cannot use search-based features (Needs you, Hub AI).
              </li>
            </ul>
          </section>

          <section id="support" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="support" />
              Support
            </h2>
            <p>
              Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. {SUPPORT_RESPONSE}{" "}
              {SUPPORT_HOURS}. More help: <Link to="/support">hub.atw.net/support</Link>.
            </p>
          </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
